import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LoggerProvider } from '@shared';
import { IsAuthenticatedUseCase } from '@auth';
import { LoadTenantComponentsUseCase } from '@features/tenant-components';
import { AppConfigService } from './app-config.service';
import { AppConfig } from './app-config.interface';
import { ScriptsProvider } from '../providers/scripts/scripts.provider';
import { EIDCProvider } from '../providers/oidc/eidc.provider';

/**
 * Application configuration initializer.
 * Loads configuration from /assets/config.json before the application starts,
 * then loads the top navbar script and initializes EID authentication.
 *
 * This is registered as an APP_INITIALIZER in app.config.ts.
 * If the configuration fails to load, the application will not start.
 *
 * @internal
 */
export function initializeAppConfig() {
  return async () => {
    const http = inject(HttpClient);
    const configService = inject(AppConfigService);
    const scriptsProvider = inject(ScriptsProvider);
    const eidcProvider = inject(EIDCProvider);
    const isAuthenticatedUseCase = inject(IsAuthenticatedUseCase);
    const loadTenantComponents = inject(LoadTenantComponentsUseCase);
    const logger = inject(LoggerProvider).createScoped('AppConfig');

    const loadTenantComponentsIfAuthenticated = async (nonLocal: boolean): Promise<void> => {
      const hasSession = nonLocal
        ? isAuthenticatedUseCase.execute() || eidcProvider.isAuthenticated()
        : isAuthenticatedUseCase.execute();
      if (!hasSession) {
        return;
      }
      try {
        await firstValueFrom(loadTenantComponents.execute());
      } catch (tenantComponentsError) {
        logger.error('Tenant components failed to load', tenantComponentsError);
      }
    };

    try {
      const config = await firstValueFrom(http.get<AppConfig>('/assets/config.json'));
      configService.setConfig(config);

      const topNavbarUrl = config.topNavbarUrl;
      try {
        await scriptsProvider.loadScript(topNavbarUrl, 'module');
      } catch (scriptError) {
        logger.error('TopNavbar failed to load script', scriptError, { url: topNavbarUrl });
        const fallbackUrl = '/products/neogov-top-navbar/prod/bundle/bundle.js';
        try {
          await scriptsProvider.loadScript(fallbackUrl, 'module');
        } catch (fallbackError) {
          logger.error('TopNavbar failed to load fallback script', fallbackError);
        }
      }

      if (config.environment === 'local') {
        await loadTenantComponentsIfAuthenticated(false);
        return;
      }

      const eidSDKScriptUrl = config.eidSDKScriptUrl || deriveEidSdkUrl(config.environment);

      if (!eidSDKScriptUrl) {
        await loadTenantComponentsIfAuthenticated(true);
        return;
      }

      try {
        await scriptsProvider.loadScript(eidSDKScriptUrl, 'text/javascript');
        await eidcProvider.initSession();
      } catch (eidError) {
        logger.error('EID failed to initialize', eidError);
      }

      await loadTenantComponentsIfAuthenticated(true);
    } catch (error) {
      logger.error('Application configuration could not be loaded', error);
      throw error;
    }
  };
}

/**
 * Derives the EID SDK script URL from environment name when not explicitly configured.
 */
function deriveEidSdkUrl(environment?: string): string | null {
  if (!environment || environment === 'local') return null;
  if (environment === 'prod') return 'https://accounts.neogov.com/EnterpriseIdentitySdk';
  return `https://accounts.${environment}.neogov.net/EnterpriseIdentitySdk`;
}
